import { FormControl } from '@common/components/form-control';
import { I18n } from 'react-redux-i18n';

import { Flex } from '@common/components/Flex';
import { getTestAttributes } from '@common/utils/tests';
import { MODULE_NAME } from '../../pedigree-import';
import { Button } from '@common/components/Button';
import { IMPORT_TYPE, FILE_DELIMITER, INDIVIDUALS_BLANK_SETTING } from '../../pedigree-import.constants';

import './configuration.styles.scss';

export const ImportConfiguration = (props) => {
  const { 
    importType, 
    onImportTypeChange, 
    fileDelimiter, 
    onFileDelimiterChange, 
    customFileDelimiter, 
    onCustomFileDelimiterChange, 
    hasHeader, 
    onHasHeaderChange, 
    onImportOptionChange,
    onShouldExistingIndividualChange,
    shouldUpdateExistingIndividual,
    onIndividualsBlankSettingChange,
    individualsBlankSetting,
    onAutoBuild,
    autoBuildDisabled,
    hasHeaderDisabled,
  } = props;

  const importTypes = React.useMemo(() => {
    return [
      {
        id: IMPORT_TYPE.pedigrees,
        label: 'Pedigrees'
      },
      {
        id: IMPORT_TYPE.individuals,
        label: 'Individuals',
      }
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const fileDelimiters = React.useMemo(() => {
    return [
      { label: 'Tab', id: FILE_DELIMITER.txtTab },
      { label: 'Comma', id: FILE_DELIMITER.txtComma },
      { label: 'Other', id: FILE_DELIMITER.txtOther },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const importOptions = React.useMemo(() => {
    return [
      { label: 'Male Value', fieldName: 'maleValue' },
      { label: 'Female Value', fieldName: 'femaleValue' },
      { label: 'Deceased Value', fieldName: 'deceasedValue' },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const handleImportTypeChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    onImportTypeChange(Number(value));
  }, [onImportTypeChange]);

  const handleCustomFileDelimiterChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    onCustomFileDelimiterChange(value);
  }, [onCustomFileDelimiterChange]);

  const handleFileDelimiterChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    if (value !== FILE_DELIMITER.txtOther) {
      onCustomFileDelimiterChange('');
    }

    onFileDelimiterChange(value);
  }, [onFileDelimiterChange, onCustomFileDelimiterChange]);

  const handleImportOptionsChange = React.useCallback((e) => {
    e.persist();

    const { value, name } = e.target;

    onImportOptionChange({
      [name]: value,
    });
  }, [onImportOptionChange]);

  const handleHasHeaderChange = React.useCallback((e) => {
    e.persist();

    const { checked } = e.target;

    onHasHeaderChange(checked);
  }, [onHasHeaderChange]);

  const handleShouldUpdateExistingIndividualChange = React.useCallback((e) => {
    e.persist();

    const { checked } = e.target;

    onShouldExistingIndividualChange(checked);
  }, [onShouldExistingIndividualChange]);

  const handleIndividualsBlankSettingChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    onIndividualsBlankSettingChange(Number(value));
  }, [onIndividualsBlankSettingChange]);
  
  return (
    <div className="PedigreeImportConfiguration">
      <Flex width="100%"  justify="space-between">
        <section className="PedigreeImportConfiguration__section">
          <h6 className="PedigreeImportConfiguration__section__title">{I18n.t('Import Type')}</h6>

          {importTypes.map((x) => (
            <Flex key={x.id} alignItems="center" justify="space-between" className="PedigreeImportConfiguration__section__item">
              <label className="PedigreeImportConfiguration__section__item__text">
                <input 
                  {...getTestAttributes(MODULE_NAME, `import-type-${x.id}`, 'input')}
                  type="radio" 
                  value={x.id}
                  checked={importType === x.id}
                  name={`${MODULE_NAME}-import-type`} 
                  onChange={handleImportTypeChange} 
                />
                {I18n.t(x.label)}
              </label>
            </Flex>
          ))}
        </section>

        <section className="PedigreeImportConfiguration__section">
          <h6 className="PedigreeImportConfiguration__section__title">{I18n.t('File Delimiter')}</h6>

          {fileDelimiters.map((x) => (
            <Flex key={x.id} alignItems="center" justify="space-between" className="PedigreeImportConfiguration__section__item">
              <label className="PedigreeImportConfiguration__section__item__text">
                <input 
                  {...getTestAttributes(MODULE_NAME, `file-delimiter-${x.id}`, 'input')}
                  type="radio" 
                  value={x.id}
                  checked={fileDelimiter === x.id}
                  name={`${MODULE_NAME}-file-delimiter`} 
                  onChange={handleFileDelimiterChange} 
                />
                {I18n.t(x.label)}
              </label>
              {x.id === FILE_DELIMITER.txtOther && (
                <FormControl
                  {...getTestAttributes(MODULE_NAME, 'custom-file-delimiter', 'input')}
                  className="PedigreeImportConfiguration__section__input"
                  disabled={fileDelimiter !== x.id}
                  value={customFileDelimiter}
                  onChange={handleCustomFileDelimiterChange}
                />
              )}
            </Flex>
          ))}
        </section>

        <section className="PedigreeImportConfiguration__section">
          <h6 className="PedigreeImportConfiguration__section__title">{I18n.t('Import Options')}</h6>

          {importOptions.map((x) => (
            <Flex key={x.fieldName} alignItems="center" justify="space-between" className="PedigreeImportConfiguration__section__item">
              <span className="PedigreeImportConfiguration__section__item__text">{I18n.t(x.label)}:&nbsp;</span>
              <FormControl
                {...getTestAttributes(MODULE_NAME, `import-option-${x.fieldName}`, 'input')}
                name={x.fieldName}
                value={props[x.fieldName]}
                className="PedigreeImportConfiguration__section__input"
                onChange={handleImportOptionsChange}
              />
            </Flex>
          ))}
        </section>
      </Flex>

      {importType == IMPORT_TYPE.individuals && (
        <div>
          <label className="PedigreeImportConfiguration__individual-setting">
            <input 
              {...getTestAttributes(MODULE_NAME, 'update-individual', 'input')}
              type="checkbox"
              checked={shouldUpdateExistingIndividual}
              onChange={handleShouldUpdateExistingIndividualChange} 
            />
            {I18n.t('Update Individual if Found')}
          </label>

          <label className="PedigreeImportConfiguration__individual-setting">
            <input 
              {...getTestAttributes(MODULE_NAME, 'individuals-blank-setting-ignore-blank', 'input')}
              disabled={!shouldUpdateExistingIndividual}
              type="radio" 
              value={INDIVIDUALS_BLANK_SETTING.dontImportBlanks}
              checked={individualsBlankSetting === INDIVIDUALS_BLANK_SETTING.dontImportBlanks}
              name={`${MODULE_NAME}-individuals-blank-setting`} 
              onChange={handleIndividualsBlankSettingChange}
            />
            {I18n.t('Ignore blank cells')}
          </label>
          <label className="PedigreeImportConfiguration__individual-setting">
            <input 
              {...getTestAttributes(MODULE_NAME, 'individuals-blank-setting-import-blank', 'input')}
              disabled={!shouldUpdateExistingIndividual}
              type="radio" 
              value={INDIVIDUALS_BLANK_SETTING.importBlanks}
              checked={individualsBlankSetting === INDIVIDUALS_BLANK_SETTING.importBlanks}
              name={`${MODULE_NAME}-individuals-blank-setting`} 
              onChange={handleIndividualsBlankSettingChange} 
            />
            {I18n.t('Import blank cells over existing data')}
          </label>
        </div>
      )}


      <p className="PedigreeImportConfiguration__file-description">
        {importType == IMPORT_TYPE.pedigrees && I18n.t('Pedigree Name, Individual Name, Mother ID, Father ID, and Gender are required fields for successfully importing a pedigree')}
        {importType == IMPORT_TYPE.individuals && I18n.t('Individual Name and Gender are required fields for successfully importing an Individual')}
      </p>

      <Flex alignItems="center">
        <label className="PedigreeImportConfiguration__has-header-label">
          <input 
            {...getTestAttributes(MODULE_NAME, 'file-has-header', 'input')}
            disabled={hasHeaderDisabled}
            type="checkbox" 
            checked={hasHeader}
            onChange={handleHasHeaderChange} 
          />
          {I18n.t('File has a Header Row')}
        </label>

        <Button 
          {...getTestAttributes(MODULE_NAME, 'auto-build', 'btn')} 
          text={I18n.t('Auto Build')} 
          disabled={autoBuildDisabled}
          className="PedigreeImportConfiguration__auto-build-btn" 
          onClick={onAutoBuild}
        />
      </Flex>
    </div>
  );
};